<template>
  <div class="email-auth">
    <h2 class="text-h5 mb-1">
      {{ $t(texts.heading) }}
    </h2>
    <p class="text-body-2 text--secondary mb-4">
      {{ $t(texts.subheading) }}
    </p>
    <v-alert
      v-if="errorKey"
      dense
      text
      type="error"
      class="mb-4 text-left"
    >
      {{ $t(errorKey) }}
    </v-alert>
    <v-alert
      v-if="resetSent"
      dense
      text
      type="success"
      class="mb-4 text-left"
    >
      {{ $t('auth.resetSent') }}
    </v-alert>
    <template v-if="mode !== 'reset'">
      <v-btn
        block
        large
        outlined
        color="primary"
        class="text-none mb-4"
        :loading="googleLoading"
        :disabled="busy"
        @click="onGoogle"
      >
        {{ $t('auth.signInWithGoogle') }}
      </v-btn>
      <div class="auth-or mb-4">
        <span>{{ $t('or') }}</span>
      </div>
    </template>
    <form
      autocomplete="on"
      @submit.prevent="onSubmit"
    >
      <v-text-field
        id="seowritee-login-email"
        v-model="email"
        outlined
        dense
        type="email"
        name="email"
        autocomplete="username"
        :label="$t('auth.email')"
        :disabled="busy"
      />
      <v-text-field
        v-if="mode !== 'reset'"
        id="seowritee-login-password"
        v-model="password"
        outlined
        dense
        name="password"
        :type="showPassword ? 'text' : 'password'"
        :autocomplete="mode === 'register' ? 'new-password' : 'current-password'"
        :label="$t('auth.password')"
        :append-icon="showPassword ? 'visibility' : 'visibility_off'"
        :disabled="busy"
        @click:append="showPassword = !showPassword"
      />
      <v-text-field
        v-if="mode === 'register'"
        id="seowritee-login-password-confirm"
        v-model="confirmPassword"
        outlined
        dense
        name="password-confirm"
        :type="showPassword ? 'text' : 'password'"
        autocomplete="new-password"
        :label="$t('auth.confirmPassword')"
        :disabled="busy"
      />
      <div
        v-if="mode === 'signIn'"
        class="d-flex justify-end mt-n2 mb-3"
      >
        <v-btn
          text
          small
          color="primary"
          class="text-none px-0"
          data-test="auth-forgot-password"
          :disabled="busy"
          @click="switchMode('reset')"
        >
          {{ $t('auth.forgotPassword') }}
        </v-btn>
      </div>
      <v-btn
        block
        large
        color="primary"
        class="text-none"
        type="submit"
        :loading="formLoading"
        :disabled="busy"
      >
        {{ $t(texts.submit) }}
      </v-btn>
    </form>
    <div class="mt-4 text-body-2">
      <template v-if="mode === 'signIn'">
        {{ $t('auth.noAccount') }}
        <v-btn
          text
          small
          color="primary"
          class="text-none px-1"
          data-test="auth-to-register"
          :disabled="busy"
          @click="switchMode('register')"
        >
          {{ $t('auth.createAccount') }}
        </v-btn>
      </template>
      <template v-else>
        <template v-if="mode === 'register'">
          {{ $t('auth.haveAccount') }}
        </template>
        <v-btn
          text
          small
          color="primary"
          class="text-none px-1"
          data-test="auth-to-sign-in"
          :disabled="busy"
          @click="switchMode('signIn')"
        >
          {{ $t(mode === 'register' ? 'auth.signIn' : 'auth.backToSignIn') }}
        </v-btn>
      </template>
    </div>
    <p
      v-if="mode !== 'reset'"
      class="text-caption text--secondary mt-4 mb-0"
    >
      {{ $t('auth.tosPrefix') }}
      <a
        :href="termsUrl"
        target="_blank"
        rel="noopener noreferrer"
      >{{ $t('auth.termsLink') }}</a>
      {{ $t('and') }}
      <a
        :href="privacyUrl"
        target="_blank"
        rel="noopener noreferrer"
      >{{ $t('auth.privacyLink') }}</a>.
    </p>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Auth} from "@/plugins/Auth";
    import {ExternalLinks} from "@/config/ExternalLinks";

    /** Sign in, create an account (#30941), or send a password reset e-mail (#30941). */
    type AuthMode = 'signIn' | 'register' | 'reset';

    /** Firebase refuses shorter passwords with auth/weak-password. */
    const MIN_PASSWORD_LENGTH = 6;

    const TEXTS: Record<AuthMode, {heading: string; subheading: string; submit: string}> = {
        signIn: {heading: 'auth.signIn', subheading: 'auth.signInWithEmail', submit: 'auth.signIn'},
        register: {heading: 'auth.createAccount', subheading: 'noCardRequired', submit: 'auth.createAccount'},
        reset: {heading: 'auth.resetTitle', subheading: 'auth.resetSubtitle', submit: 'auth.sendResetLink'},
    };

    export default Vue.extend({
        name: "FirebaseAuthUi",
        data() {
            return {
                mode: 'signIn' as AuthMode,
                email: '',
                password: '',
                confirmPassword: '',
                showPassword: false,
                errorKey: '',
                resetSent: false,
                googleLoading: false,
                formLoading: false,
            };
        },
        computed: {
            busy(): boolean {
                return this.googleLoading || this.formLoading;
            },
            texts(): {heading: string; subheading: string; submit: string} {
                return TEXTS[this.mode];
            },
            termsUrl(): string {
                return ExternalLinks.termsAndConditions;
            },
            privacyUrl(): string {
                return ExternalLinks.privacyPolicy;
            },
        },
        methods: {
            switchMode(mode: AuthMode): void {
                this.mode = mode;
                this.errorKey = '';
                this.resetSent = false;
                this.password = '';
                this.confirmPassword = '';
            },
            async onGoogle(): Promise<void> {
                this.errorKey = '';
                this.resetSent = false;
                this.googleLoading = true;
                try {
                    await Auth.signInWithGoogle();
                } catch (error) {
                    this.errorKey = typeof error === 'string' ? error : 'auth.errors.googleFailed';
                } finally {
                    this.googleLoading = false;
                }
            },
            /** The error key for input Firebase would refuse anyway, or ''. */
            invalidInput(): string {
                if (!this.email.trim() || !this.email.includes('@')) {
                    return 'auth.errors.invalidEmail';
                }
                if (this.mode === 'reset') {
                    return '';
                }
                if (!this.password) {
                    return 'auth.errors.passwordRequired';
                }
                if (this.mode === 'register' && this.password.length < MIN_PASSWORD_LENGTH) {
                    return 'auth.errors.weakPassword';
                }
                if (this.mode === 'register' && this.password !== this.confirmPassword) {
                    return 'auth.errors.passwordMismatch';
                }
                return '';
            },
            async onSubmit(): Promise<void> {
                this.resetSent = false;
                this.errorKey = this.invalidInput();
                if (this.errorKey) {
                    return;
                }
                this.formLoading = true;
                try {
                    if (this.mode === 'reset') {
                        await Auth.sendPasswordReset(this.email);
                        this.resetSent = true;
                    } else if (this.mode === 'register') {
                        await Auth.signUpWithEmail(this.email, this.password);
                    } else {
                        await Auth.signInWithEmail(this.email, this.password);
                    }
                } catch (error) {
                    this.errorKey = typeof error === 'string' ? error : 'auth.errors.network';
                } finally {
                    this.formLoading = false;
                }
            },
        },
    });
</script>

<style lang="sass" scoped>
.email-auth
    text-align: center
    max-width: 100%

.auth-or
    display: flex
    align-items: center
    color: var(--v-greyTint-base, #8C8C8C)
    font-size: 0.75rem
    text-transform: uppercase
    letter-spacing: 0.08em

    &::before,
    &::after
        content: ''
        flex: 1
        height: 1px
        background: currentColor
        opacity: 0.35

    span
        padding: 0 12px
</style>
